import { refCount, publishReplay, finalize, take, map, concatAll, tap, concatMap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type { Visualize3dObject } from '../models/visualize3d-object';
import { QModelComponent } from '../v3d-components/q-model-component';
import { Observable, of } from 'rxjs';
import { ViewPoint } from '../models/view-point';
import { CircleSegments } from '../models/circle-segments';
import type { QmodelJsonObject } from '../models/qmodel/qmodel-json-object';
import { ConfigService } from './config.service';
import { DxfExportMode } from '../models/dxf-export-mode';
import { QModelViewMode } from '../models/q-model-view-mode';

type Export3dMethod = 'qmdjson';

@Injectable()
export class ApiService {
  private httpClient = inject(HttpClient);
  private configService = inject(ConfigService);

  private cache = new Map();

  public isLoading: boolean;

  /**
   * Returns an Observable which completes with the current API url.
   */
  getCurrentApiUrl(): Observable<string> {
    // .take(1) because we only want the current value, not a perpetual subscription
    return this.configService.getApiRoot().pipe(take(1));
  }

  /**
   * Internal helper function for running a function with the current api root url.
   * @param fn Function which receives the apiRoot as param and returns an Observable<T>
   * @example
   * return this.withApiUrl(apiRoot => console.info('API root is ' + apiRoot);
   * // Logs 'API root is https://whatever-the-api-url.is/api/'
   */
  private withApiUrl<T>(fn: (apiRoot: string) => Observable<T>): Observable<T> {
    return this.getCurrentApiUrl().pipe(concatMap(apiRoot => fn(apiRoot)));
  }

  public clearCache(): void {
    this.cache = new Map();
  }

  exportDxf(
    v3dObject: Visualize3dObject,
    options?: {
      dxfExportMode: DxfExportMode;
      view?: ViewPoint;
      circleSegments?: CircleSegments;
      circleSegmentationAngle?: number;
      viewPortX?: number;
      viewPortY?: number;
      viewPortZ?: number;
      layers?: string | null;
      pens?: string | null;
    },
  ): Observable<ArrayBuffer> {
    const q = this.buildQueryString(options);
    return this.withApiUrl(apiRoot =>
      this.httpClient.post(`${apiRoot}/object-3d/export/dxf${q}`, v3dObject, { responseType: 'arraybuffer' }),
    );
  }

  exportImage(
    v3dObject: Visualize3dObject,
    options: {
      format: string;
      width?: number;
      height?: number;
      view?: ViewPoint;
      circleSegments?: CircleSegments;
      backgroundColor?: string;
      renderMode?: QModelViewMode;
      selectedColor?: string | null;
      scaleX?: number;
      scaleY?: number;
      viewPortX?: number;
      viewPortY?: number;
      viewPortZ?: number;
      pens?: string | null;
    },
  ): Observable<ArrayBuffer> {
    const q = this.buildQueryString(options);
    return this.withApiUrl(apiRoot =>
      this.httpClient.post(`${apiRoot}/object-3d/export/image/${options.format}${q}`, v3dObject, { responseType: 'arraybuffer' }),
    );
  }

  exportSvg(
    v3dObject: Visualize3dObject,
    options: {
      width?: number;
      height?: number;
      view?: ViewPoint;
      circleSegments?: CircleSegments;
      backgroundColor?: string;
      scaleX?: number;
      scaleY?: number;
      viewPortX?: number;
      viewPortY?: number;
      viewPortZ?: number;
      pens?: string | null;
    },
  ) {
    const q = this.buildQueryString(options);
    return this.withApiUrl(apiRoot =>
      this.httpClient.post(`${apiRoot}/object-3d/export/image/svg${q}`, v3dObject, { responseType: 'arraybuffer' }),
    );
  }

  exportGmModel(
    v3dObject: Visualize3dObject,
    options?: {
      view?: ViewPoint;
      viewPortX?: number;
      viewPortY?: number;
      viewPortZ?: number;
      pens?: string | null;
    },
  ): Observable<ArrayBuffer> {
    const q = this.buildQueryString(options);
    return this.withApiUrl(apiRoot =>
      this.httpClient.post(`${apiRoot}/object-3d/export/gmmodel${q}`, v3dObject, { responseType: 'arraybuffer' }),
    );
  }

  exportQmdJson(qmodel: QModelComponent): Observable<QmodelJsonObject> {
    return this.export3D<QmodelJsonObject>(qmodel, 'qmdjson');
  }

  getQModelIds(): Observable<string[]> {
    return this.withApiUrl(apiRoot => this.httpClient.get<string[]>(`${apiRoot}/q-model`));
  }

  private export3D<T = any>(component: QModelComponent, method: Export3dMethod): Observable<T> {
    const cacheKey = this._getCacheKey(component);
    const cachedObservable = this.cache.get(cacheKey);
    if (cachedObservable) {
      return cachedObservable;
    }

    this.isLoading = true;

    return this.build3dExportUrl(component, method).pipe(
      map(url => {
        return this.httpClient.get<T>(url).pipe(
          finalize(() => (this.isLoading = false)),
          publishReplay(1),
          refCount(),
        );
      }),
      tap(observable => this.cache.set(cacheKey, observable)),
      concatAll(),
    );
  }

  exportQModel(object3d: Visualize3dObject): Observable<ArrayBuffer> {
    return this.withApiUrl(apiRoot =>
      this.httpClient.post(`${apiRoot}/object-3d/export/qmodel`, object3d, { responseType: 'arraybuffer' }),
    );
  }

  private build3dExportUrl(component: QModelComponent, method: Export3dMethod): Observable<string> {
    // btoa-function does not always correctly encode special characters, so we use this solution instead.
    // More details: https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
    const safeEncodeToBase64 = (source: string): string => {
      return btoa(
        encodeURIComponent(source).replace(/%([0-9A-F]{2})/g, (_, p1) => {
          return String.fromCharCode(parseInt('0x' + p1));
        }),
      );
    };

    const queryObj = {
      parameters: component.parameters,
      tags: component.tags,
      detailMode: component.detailMode,
      lodLevel: component.lodLevel,
      platform: component.platform,
      viewMode: component.viewMode,
      dimensionLineMode: component.dimensionLineMode,
      calculationResolution: component.calculationResolution,
      throwOnQpdErrors: this.configService.getThrowOnQpdErrors(),
    };
    const base64Encoded = safeEncodeToBase64(JSON.stringify(queryObj));
    return this.withApiUrl(apiRoot => of(`${apiRoot}/q-model/${component.id}/export/${method}?query=${base64Encoded}`));
  }

  private _getCacheKey(component: QModelComponent): string {
    return JSON.stringify(component);
  }

  private buildQueryString(obj: Record<string, unknown>): string {
    let query = '';
    if (!obj) {
      return query;
    }
    for (const key of Object.keys(obj)) {
      if (query === '') {
        query = '?';
      } else {
        query += '&';
      }
      query += `${key}=${obj[key]}`;
    }
    return query;
  }
}
